import { InstrumentoFinanceiroApiResponse } from 'common/genericTypes';
import { getInstrumentoFinanceiroShortDescription } from 'common/genericDescriptions';
import { AutoComplete } from 'm2m.components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, Grid, Paper } from '@material-ui/core';
import _ from 'lodash';
import { getDataEmissaoDisplay, getVolumeEmissaoDisplay, getSafeDataEmissao } from 'utils/instrumentoFinanceiroUtils';

interface Props {
  instrumentos: Array<InstrumentoFinanceiroApiResponse>;
  instrumento: InstrumentoFinanceiroApiResponse | null;
  vertical?: boolean;
  label?: string | null;
  disabled?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  onInputChange?: (value: string) => void;
  onLoadMore?: () => void;
  onChange: (instrumento: InstrumentoFinanceiroApiResponse | null) => void;
}

const InstrumentoAutocomplete: React.FC<Props> = (props) => {
  const { instrumentos, instrumento, vertical, label, disabled, loading, onInputChange, hasMore, onLoadMore, onChange } = props;
  const [inputValue, setInputValue] = useState<string>('');
  const lastNotifiedRef = useRef<string>('');

  const filterInstrumentos = (options: Array<InstrumentoFinanceiroApiResponse>, value: string) => {
    var valueUpperCase = value.toUpperCase();
    return options.filter(
      (op) =>
        op?.codigo?.toLocaleUpperCase().includes(valueUpperCase.trim()) ||
        op?.razaoSocialEmissor?.toLocaleUpperCase().includes(valueUpperCase) ||
        op?.emissor?.razaoSocial?.toLocaleUpperCase().includes(valueUpperCase)
    );
  };

  // Notificar o caller sobre mudanças no input (para busca server-side)
  useEffect(() => {
    if (onInputChange && inputValue !== lastNotifiedRef.current) {
      // Não notificar se o valor é o código do item selecionado (seleção, não digitação)
      if (instrumento && inputValue === instrumento.codigo) return;
      lastNotifiedRef.current = inputValue;
      onInputChange(inputValue);
    }
  }, [inputValue, onInputChange, instrumento]);

  const handleInputChange = useCallback((newValue: string) => {
    setInputValue(newValue);
  }, []);

  const width = vertical ? '400px' : '500px';
  const itemXs = vertical ? 4 : 6;

  return (
    <AutoComplete
      options={_.orderBy(instrumentos, (x) => getSafeDataEmissao(x), 'desc')}
      getOptionLabel={(option: InstrumentoFinanceiroApiResponse) => option.codigo}
      filterOptions={onInputChange ? (options: Array<InstrumentoFinanceiroApiResponse>) => options : (options: Array<InstrumentoFinanceiroApiResponse>, state) =>
        filterInstrumentos(options, state.inputValue)
      }
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loading={loading}
      value={instrumento}
      onChange={onChange}
      disabled={disabled ?? false}
      filterSelectedOptions
      label={label ?? 'Instrumento para alocação *'}
      placeholder={'Pesquisar...'}
      PaperComponent={({ children }) => (
        <Paper style={{ width, height: '100%' }}>
          {children}
          {hasMore && onLoadMore && (
            <div
              style={{ padding: '8px 16px', textAlign: 'center', borderTop: '1px solid #e0e0e0', cursor: 'pointer' }}
              onMouseDown={(e) => {
                e.preventDefault();
                onLoadMore();
              }}
            >
              <Typography variant="body2" color="primary" style={{ fontWeight: 600 }}>
                {'Carregar mais resultados...'}
              </Typography>
            </div>
          )}
        </Paper>
      )}
      renderOption={(option: InstrumentoFinanceiroApiResponse) => (
        <div style={{ width: '100%' }}>
          <Grid container>
            <Grid item xs={itemXs}>
              <div>
                <Typography style={{ margin: 'auto' }} variant="subtitle2">
                  {'Data da Emissão'}
                </Typography>
              </div>
              <div>
                <Typography style={{ margin: 'auto' }}>
                  {getDataEmissaoDisplay(option)}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={itemXs}>
              <div>
                <Typography style={{ margin: 'auto' }} variant="subtitle2">
                  {'Número da Emissão'}
                </Typography>
              </div>
              <div>
                <Typography style={{ margin: 'auto' }}>{option.numeroEmissao}</Typography>
              </div>
            </Grid>
            <Grid item xs={itemXs}>
              <div>
                <Typography style={{ margin: 'auto' }} variant="subtitle2">
                  {'Emissor'}
                </Typography>
              </div>
              <div>
                <Typography style={{ margin: 'auto' }}>{option.razaoSocialEmissor}</Typography>
              </div>
            </Grid>
            <Grid item xs={itemXs}>
              <div>
                <Typography style={{ margin: 'auto' }} variant="subtitle2">
                  {'Valor da Emissão'}
                </Typography>
              </div>
              <div>
                <Typography style={{ margin: 'auto' }}>
                  {getVolumeEmissaoDisplay(option)}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={itemXs}>
              <div>
                <Typography style={{ margin: 'auto' }} variant="subtitle2">
                  {'Instrumento'}
                </Typography>
              </div>
              <div>
                <Typography style={{ margin: 'auto' }}>
                  {getInstrumentoFinanceiroShortDescription[option.tipoInstrumentoFinanceiro]}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={itemXs}>
              <div>
                <Typography style={{ margin: 'auto' }} variant="subtitle2">
                  {'Código do Ativo'}
                </Typography>
              </div>
              <div>
                <Typography style={{ margin: 'auto' }}>{option.codigo}</Typography>
              </div>
            </Grid>
          </Grid>
        </div>
      )}
      helperText={instrumento ? `${instrumento.numeroEmissao} - ${instrumento.razaoSocialEmissor}` : undefined}
    />
  );
};

export default InstrumentoAutocomplete;
